import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CloudSyncOutlined,
  DeleteOutlined,
  PlusOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Input,
  InputNumber,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import { API } from '@/services/api.service';
import { OWNER_TYPES, date, errorText } from './format';

const { Text } = Typography;

const emptyOwner = () => ({
  name: '',
  seat: '',
  foreign: true,
  countryName: '',
  countryCode: null,
  ownerType: 54,
  share: 0,
  mb: '',
  source: 'MANUAL',
});

/**
 * Members (Table 1) and the obligor's own shares in domestic companies
 * (Table 2). Members come from APR; a country APR spells differently, or a
 * branch's founder, is fixed here by hand.
 */
export default function OwnersTab({ data, companyId, onSaved }) {
  const { t } = useTranslation('diReports');
  const [owners, setOwners] = useState([]);
  const [holdings, setHoldings] = useState([]);
  const p = data.profile;

  useEffect(() => {
    setOwners((p.owners || []).map((o, i) => ({ ...o, key: i })));
    setHoldings((p.holdings || []).map((h, i) => ({ ...h, key: i })));
  }, [p]);

  const { data: countries = [] } = useQuery({
    queryKey: ['di-countries'],
    queryFn: () => API.diReports.getCountries(),
    staleTime: Infinity,
  });
  const countryOptions = useMemo(
    () =>
      countries.map((c) => ({
        value: c.code,
        label: `${c.code} · ${c.name} (${c.a2})`,
      })),
    [countries]
  );

  // S001… as the form will number them (largest share first).
  const codeOf = useMemo(() => {
    const map = new Map();
    for (const o of data.form.owners || []) map.set(o.name, o.code);
    return map;
  }, [data]);

  const update = (key, patch) =>
    setOwners((list) =>
      list.map((o) => (o.key === key ? { ...o, ...patch } : o))
    );
  const updateHolding = (key, patch) =>
    setHoldings((list) =>
      list.map((h) => (h.key === key ? { ...h, ...patch } : h))
    );

  const saveMutation = useMutation({
    mutationFn: () =>
      API.diReports.saveProfile(companyId, {
        owners: owners.map(({ key: _key, ...o }) => o),
        holdings: holdings.map(({ key: _key, ...h }) => h),
      }),
    onSuccess: () => {
      message.success(t('messages.saved'));
      onSaved();
    },
    onError: async (e) => message.error(await errorText(e)),
  });

  const aprMutation = useMutation({
    mutationFn: () => API.diReports.refreshFromApr(companyId),
    onSuccess: () => {
      message.success(t('messages.aprDone'));
      onSaved();
    },
    onError: async (e) => message.error(await errorText(e)),
  });

  const total = owners.reduce((s, o) => s + (Number(o.share) || 0), 0);
  // D001… for direct holdings in list order, D001-1… under them — as the form numbers them.
  const holdingCodes = useMemo(() => {
    const codes = new Map();
    holdings
      .filter((h) => !h.via)
      .forEach((h, i) =>
        codes.set(h.key, `D${String(i + 1).padStart(3, '0')}`)
      );
    const counters = {};
    holdings
      .filter((h) => h.via)
      .forEach((h) => {
        counters[h.via] = (counters[h.via] || 0) + 1;
        codes.set(h.key, `${h.via}-${counters[h.via]}`);
      });
    return codes;
  }, [holdings]);
  const directCodes = holdings
    .filter((h) => !h.via)
    .map((h) => holdingCodes.get(h.key));

  const ownerColumns = [
    {
      title: '',
      key: 'code',
      width: 70,
      render: (_, o) =>
        o.foreign ? (
          <Text strong>
            {codeOf.get(o.name) || (o.share < 10 ? t('owners.portfolio') : '')}
          </Text>
        ) : null,
    },
    {
      title: t('owners.name'),
      dataIndex: 'name',
      width: 260,
      render: (v, o) => (
        <div className="flex flex-col gap-1">
          <Input
            value={v}
            onChange={(e) => update(o.key, { name: e.target.value })}
          />
          <Input
            size="small"
            placeholder={t('owners.seat')}
            value={o.seat}
            onChange={(e) => update(o.key, { seat: e.target.value })}
          />
        </div>
      ),
    },
    {
      title: t('owners.foreign'),
      dataIndex: 'foreign',
      width: 70,
      render: (v, o) => (
        <Switch
          checked={v}
          onChange={(checked) => update(o.key, { foreign: checked })}
        />
      ),
    },
    {
      title: t('owners.country'),
      dataIndex: 'countryCode',
      width: 210,
      render: (v, o) =>
        o.foreign ? (
          <div>
            <Select
              showSearch
              optionFilterProp="label"
              status={!v ? 'error' : undefined}
              value={v}
              options={countryOptions}
              onChange={(code) => update(o.key, { countryCode: code })}
              style={{ width: '100%' }}
            />
            {o.countryName && (
              <div className="text-xs opacity-60">APR: {o.countryName}</div>
            )}
          </div>
        ) : (
          <Input
            size="small"
            placeholder={t('owners.mb')}
            value={o.mb}
            onChange={(e) => update(o.key, { mb: e.target.value })}
          />
        ),
    },
    {
      title: t('owners.type'),
      dataIndex: 'ownerType',
      width: 190,
      render: (v, o) =>
        o.foreign ? (
          <Select
            value={v}
            options={OWNER_TYPES.map((x) => ({
              value: x,
              label: `${x} — ${t(`ownerType.${x}`)}`,
            }))}
            onChange={(x) => update(o.key, { ownerType: x })}
            style={{ width: '100%' }}
            popupMatchSelectWidth={false}
          />
        ) : (
          <Tag bordered={false}>{t('owners.domestic')}</Tag>
        ),
    },
    {
      title: `${t('owners.share')} %`,
      dataIndex: 'share',
      width: 100,
      render: (v, o) => (
        <InputNumber
          min={0}
          max={100}
          step={0.01}
          value={v}
          onChange={(x) => update(o.key, { share: x ?? 0 })}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: '',
      key: 'remove',
      width: 48,
      render: (_, o) => (
        <Tooltip title={t('actions.remove')}>
          <Button
            shape="circle"
            size="small"
            icon={<DeleteOutlined />}
            onClick={() => setOwners((l) => l.filter((x) => x.key !== o.key))}
          />
        </Tooltip>
      ),
    },
  ];

  const holdingColumns = [
    {
      title: '',
      key: 'code',
      width: 80,
      render: (_, h) => <Text strong>{holdingCodes.get(h.key)}</Text>,
    },
    {
      title: t('owners.mb'),
      dataIndex: 'mb',
      width: 130,
      render: (v, h) => (
        <Input
          value={v}
          maxLength={8}
          onChange={(e) => updateHolding(h.key, { mb: e.target.value })}
        />
      ),
    },
    {
      title: t('holdings.name'),
      dataIndex: 'name',
      render: (v, h) => (
        <Input
          value={v}
          onChange={(e) => updateHolding(h.key, { name: e.target.value })}
        />
      ),
    },
    {
      title: `${t('owners.share')} %`,
      dataIndex: 'share',
      width: 100,
      render: (v, h) => (
        <InputNumber
          min={0}
          max={100}
          step={0.01}
          value={v}
          onChange={(x) => updateHolding(h.key, { share: x ?? 0 })}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: t('holdings.via'),
      dataIndex: 'via',
      width: 140,
      render: (v, h) => (
        <Select
          allowClear
          value={v || undefined}
          placeholder={t('holdings.direct')}
          options={directCodes.map((c) => ({ value: c, label: c }))}
          onChange={(x) => updateHolding(h.key, { via: x || '' })}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: '',
      key: 'remove',
      width: 50,
      render: (_, h) => (
        <Button
          shape="circle"
          size="small"
          icon={<DeleteOutlined />}
          onClick={() => setHoldings((l) => l.filter((x) => x.key !== h.key))}
        />
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <Button
          shape="round"
          icon={<CloudSyncOutlined />}
          loading={aprMutation.isPending}
          onClick={() => aprMutation.mutate()}
        >
          {t('actions.refreshApr')}
        </Button>
        <span className="text-sm opacity-70">
          {t('owners.aprLine', {
            status: p.apr?.status || '—',
            date: date(p.apr?.fetchedAt),
            capital: (p.capital?.registered || 0).toLocaleString('sr-RS'),
          })}
        </span>
        {p.apr?.error && <Tag color="red">{p.apr.error}</Tag>}
      </div>
      {aprMutation.isPending && (
        <Alert
          type="info"
          showIcon
          className="!static"
          message={t('owners.aprWait')}
        />
      )}

      <Table
        rowKey="key"
        size="small"
        pagination={false}
        tableLayout="fixed"
        scroll={{ x: 950 }}
        columns={ownerColumns}
        dataSource={owners}
        footer={() => (
          <div className="flex items-center justify-between">
            <Button
              shape="round"
              size="small"
              icon={<PlusOutlined />}
              onClick={() =>
                setOwners((l) => [...l, { ...emptyOwner(), key: Date.now() }])
              }
            >
              {t('owners.add')}
            </Button>
            <span className={total > 100.01 ? 'text-red-600' : ''}>
              {t('owners.total', { total: total.toFixed(2) })}
            </span>
          </div>
        )}
      />
      <Text type="secondary" className="text-xs">
        {t('owners.help')}
      </Text>

      <div>
        <Text strong>{t('holdings.title')}</Text>
        <div className="mb-2 text-xs opacity-70">{t('holdings.help')}</div>
        {data.suggestedHoldings?.length > 0 && (
          <Alert
            type="warning"
            showIcon
            className="!static mb-2"
            message={
              <Space direction="vertical" size={2}>
                {data.suggestedHoldings.map((h) => (
                  <span key={h.mb}>
                    {t('holdings.suggested', { name: h.name, share: h.share })}{' '}
                    <a
                      onClick={() =>
                        setHoldings((l) => [
                          ...l,
                          { ...h, via: '', key: Date.now() },
                        ])
                      }
                    >
                      {t('actions.add')}
                    </a>
                  </span>
                ))}
              </Space>
            }
          />
        )}
        <Table
          rowKey="key"
          size="small"
          pagination={false}
          tableLayout="fixed"
          columns={holdingColumns}
          dataSource={holdings}
          locale={{ emptyText: t('holdings.none') }}
          footer={() => (
            <Button
              shape="round"
              size="small"
              icon={<PlusOutlined />}
              onClick={() =>
                setHoldings((l) => [
                  ...l,
                  { mb: '', name: '', share: 0, via: '', key: Date.now() },
                ])
              }
            >
              {t('holdings.add')}
            </Button>
          )}
        />
      </div>

      <div className="flex justify-end">
        <Button
          shape="round"
          type="primary"
          icon={<SaveOutlined />}
          loading={saveMutation.isPending}
          onClick={() => saveMutation.mutate()}
        >
          {t('actions.save')}
        </Button>
      </div>
    </div>
  );
}
